/**
 * The three looks' stylesheet. Every rule is scoped to `.pp` (a page drawn by
 * proposalPages/render.js) or to a look class (`look-editorial`, `look-swiss`,
 * `look-bold`) on an ancestor, so a bid response, which still prints on the
 * warm-ground layout, is untouched, and the public page can carry these rules
 * inside the app without them reaching anything else.
 *
 * Measured from the Figma frames at 96dpi: an 816 × 1056 page; side margins of
 * 72px (Editorial), 56px (Swiss grid) and 64px (Bold red); Public Sans; white,
 * black and red only; square corners; no rule lines (the two signature lines
 * are the exception); soft panels for grouping, running 24px past the text
 * margin; big numbers drawn as outlines; a closing panel at 822px and the
 * footer at 1006px.
 *
 * Order matters: the per-look heading rules come before the per-page rules,
 * so a page can set its own spacing under the heading in every look.
 */

const RED = '#EF4237'
const INK = '#1A1A1A'
const MUTED = '#6B6B69'
const PANEL = '#F5F4F2'
const PINK = '#FDEFED'
const SANS = `'Public Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif`

export const LOOK_CSS = `
  body.look-editorial, body.look-swiss, body.look-bold { font-family: ${SANS}; color: ${INK}; }
  @media screen {
    body.look-editorial, body.look-swiss, body.look-bold { background: #EDECE9; padding: 24px 0; }
    body.look-editorial .page.pp, body.look-swiss .page.pp, body.look-bold .page.pp { margin: 0 auto 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06); }
  }

  .page.pp {
    width: 816px; height: 1056px; min-height: 1056px; max-height: 1056px; padding: 0; box-sizing: border-box;
    background: #FFFFFF; color: ${INK}; font-family: ${SANS}; line-height: 1.4;
    position: relative; overflow: hidden; break-after: page; page-break-after: always; break-inside: avoid;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; text-wrap: pretty;
    --m: 72px; --top: 128px; --panel: ${PANEL}; --cw: 200px;
  }
  .look-swiss .page.pp { --m: 56px; --top: 104px; --cw: 211px; }
  .look-bold .page.pp { --m: 64px; --top: 112px; --cw: 205px; --panel: ${PINK}; }
  .page.pp:last-child { break-after: auto; page-break-after: auto; }
  .pp *, .pp *::before, .pp *::after { box-sizing: border-box; }
  .pp h1, .pp h2, .pp h3 { margin: 0; font-family: ${SANS}; text-wrap: balance; }
  .pp p { margin: 0; }
  .pp ul { margin: 0; padding: 0; list-style: none; }
  .pp svg { display: block; }
  .pp .pp-body { position: absolute; left: var(--m); right: var(--m); top: var(--top); z-index: 1; }
  .pp .pp-p { font-family: ${SANS}; font-size: 13px; line-height: 1.55; }

  /* Outlined numbers: the one display treatment, used for every big figure. */
  .pp .onum {
    color: transparent; -webkit-text-stroke: 1.5px ${INK};
    font-family: ${SANS}; font-weight: 900; font-variant-numeric: lining-nums; letter-spacing: -0.02em;
  }
  .pp .onum.red { color: transparent; -webkit-text-stroke-color: ${RED}; }
  .pp .onum.white { color: transparent; -webkit-text-stroke-color: #FFFFFF; }
  .look-swiss .pp .onum, .look-bold .pp .onum { -webkit-text-stroke-width: 1.3px; }
  @supports not (-webkit-text-stroke: 1px black) { .pp .onum { color: ${INK} !important; } .pp .onum.red { color: ${RED} !important; } .pp .onum.white { color: #fff !important; } }

  /* ── Furniture: chapter label, footer, closing panel ─────────────────── */

  .pp .pp-kicker {
    position: absolute; left: var(--m); top: 72px; display: flex; align-items: baseline; z-index: 2;
    font-size: 11px; line-height: 13px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: ${INK};
  }
  .pp .pp-kicker .n { width: 36px; color: ${RED}; font-weight: 700; letter-spacing: 0.04em; }

  .pp .pp-foot {
    position: absolute; left: var(--m); right: var(--m); top: 1006px; height: 20px; z-index: 2;
    display: flex; align-items: center; font-size: 9px; line-height: 11px; color: ${MUTED};
  }
  .pp .pp-foot .mark { flex: none; width: 20px; height: 20px; }
  .pp .pp-foot-t { margin-left: 10px; }
  .pp .pp-foot-n { margin-left: auto; font-weight: 600; color: ${INK}; }
  .look-swiss .pp .pp-foot-n, .look-bold .pp .pp-foot-n { font-weight: 700; }

  .pp .pp-closer {
    position: absolute; left: calc(var(--m) - 24px); right: calc(var(--m) - 24px); top: 822px; height: 150px; z-index: 1;
    background: var(--panel); padding: 20px 24px 0; overflow: hidden;
  }
  .pp .pp-closer-h { font-size: 14px; line-height: 16px; font-weight: 700; margin-bottom: 18px; }
  .pp .pp-trio { display: grid; grid-template-columns: repeat(3, var(--cw)); column-gap: 24px; }
  .pp .pp-closer-k { font-size: 12.5px; line-height: 1.3; font-weight: 700; color: ${RED}; height: 38px; overflow: hidden; }
  .look-swiss .pp .pp-closer-k { color: ${INK}; }
  .pp .pp-closer-v { font-size: 11.5px; line-height: 1.5; color: ${MUTED}; }

  /* ── Type ──────────────────────────────────────────────────────────── */

  .pp .pp-head { margin-bottom: 48px; }
  .pp .pp-head-plain { margin-bottom: 40px; }
  .pp .pp-h1 { font-size: 36px; line-height: 1.1; font-weight: 900; letter-spacing: -0.01em; color: ${INK}; max-width: 672px; }
  .pp .pp-h1-l { font-size: 40px; line-height: 1.05; }
  .pp .pp-h1-m { font-size: 32px; line-height: 1.08; }
  .pp .pp-h1-xl { font-size: 56px; line-height: 1.15; letter-spacing: -0.02em; }
  .pp .pp-h1-glance { line-height: 1.05; }
  .pp .pp-h1-glance.long { font-size: 28px; }
  .pp .pp-sub { font-size: 14px; line-height: 1.55; color: ${MUTED}; margin-top: 12px; max-width: 672px; }
  .pp .pp-label {
    font-size: 10px; line-height: 12px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: ${MUTED};
  }
  .pp .pp-label.red, .pp .red { color: ${RED}; }
  .pp .pp-muted { color: ${MUTED}; font-size: 13px; line-height: 1.5; }
  .pp .pp-small { font-size: 12px; line-height: 1.4; color: ${MUTED}; margin-top: 18px; }
  .pp .pp-lead { font-size: 15px; font-weight: 700; line-height: 1.4; margin: 12px 0 16px; }
  .pp .pp-prose p { font-size: 13px; line-height: 1.65; margin-bottom: 10px; }
  .pp .pp-cols2 { display: grid; grid-template-columns: 1fr 1fr; column-gap: 24px; }
  .pp .pp-cols4 { display: grid; grid-template-columns: repeat(4, 1fr); column-gap: 20px; }

  /* Panels: the only grouping device, 24px past the text margin. */
  .pp .pp-panel { background: var(--panel); margin: 0 -24px 36px; padding: 22px 24px; }
  .pp .pp-panel-h { font-size: 15px; line-height: 18px; font-weight: 700; margin-bottom: 22px; }
  .look-swiss .pp .pp-panel-h, .look-bold .pp .pp-panel-h { font-size: 14px; line-height: 16px; }
  .pp .pp-trio-k { font-size: 13px; line-height: 1.3; font-weight: 700; margin-bottom: 7px; color: ${INK}; }
  .pp .pp-trio-k.red { color: ${RED}; }
  .pp .pp-trio-v { font-size: 12.5px; line-height: 1.5; color: ${MUTED}; }
  .look-swiss .pp .pp-trio-v, .look-bold .pp .pp-trio-v { font-size: 12px; }

  /* Bullets: a small red square, or the red dash. */
  .pp .pp-sq li { position: relative; padding-left: 16px; font-size: 13px; line-height: 15px; margin-bottom: 13px; }
  .pp .pp-sq li::before { content: ''; position: absolute; left: 0; top: 6px; width: 5px; height: 5px; background: ${RED}; }
  .pp .pp-dash li { position: relative; padding-left: 24px; font-size: 14px; line-height: 16px; margin-bottom: 14px; }
  .pp .pp-dash li::before { content: '—'; position: absolute; left: 0; color: ${RED}; }
  .pp .pp-dash.grey li::before { color: ${MUTED}; }

  /* ── Swiss: the black bar and the giant number beside the headline ─── */

  .look-swiss .pp .pp-bar {
    position: absolute; left: 0; right: 0; top: 0; height: 64px; display: flex; align-items: center; z-index: 2;
    padding: 0 56px; background: ${INK}; color: #FFFFFF;
    font-size: 12px; line-height: 14px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
  }
  .look-swiss .pp .pp-bar .n { width: 44px; color: ${RED}; font-size: 14px; font-weight: 900; letter-spacing: 0; }
  .look-swiss .pp .pp-bar .c { margin-left: auto; font-weight: 400; letter-spacing: 0; text-transform: none; color: #9E9E99; font-size: 11px; }
  .look-swiss .pp .pp-head { display: grid; grid-template-columns: 256px 454px; align-items: start; margin: -12px 0 64px -6px; min-height: 120px; }
  .look-swiss .pp .pp-head-plain { display: block; margin: 0 0 76px; min-height: 0; }
  .look-swiss .pp .pp-head-t { padding-top: 12px; }
  .look-swiss .pp .pp-bignum { font-size: 120px; line-height: 120px; letter-spacing: -0.05em; -webkit-text-stroke-width: 2px; }
  .look-swiss .pp .pp-h1 { font-size: 30px; line-height: 1.08; max-width: 454px; }
  .look-swiss .pp .pp-head-plain .pp-h1 { max-width: 704px; }
  .look-swiss .pp .pp-sub { font-size: 13px; margin-top: 12px; max-width: 454px; }

  /* ── Bold: red label, giant red number bleeding off the top right ──── */

  .look-bold .pp .pp-kicker { top: 64px; font-size: 12px; line-height: 14px; color: ${RED}; font-weight: 700; letter-spacing: 0.12em; }
  .look-bold .pp .pp-giant {
    position: absolute; top: -50px; left: 516px; z-index: 0; white-space: nowrap;
    font-size: 260px; line-height: 260px; letter-spacing: -0.08em; -webkit-text-stroke-width: 2px;
  }
  .look-bold .pp .pp-head { margin-top: 58px; }
  .look-bold .pp .pp-h1 { position: relative; z-index: 1; font-size: 36px; line-height: 1.06; letter-spacing: -0.012em; max-width: 540px; }
  .look-bold .pp .pp-sub { font-size: 13px; margin-top: 14px; max-width: 688px; }

  /* ── Cover ─────────────────────────────────────────────────────────── */

  .pp.pp-cover { padding: 0; }
  .pp .pp-cover-h1 {
    position: absolute; font-size: 64px; line-height: 0.96; font-weight: 900; letter-spacing: -0.02em; margin: 0; text-wrap: balance;
  }
  .pp .pp-cover-h1.s { font-size: 52px; line-height: 1; }
  .pp .pp-cover-h1.xs { font-size: 40px; line-height: 1.05; }
  .cover-editorial .pp-cover-lockup { position: absolute; left: 72px; top: 64px; }
  .cover-editorial .pp-cover-ref { position: absolute; right: 72px; top: 86px; font-size: 11px; line-height: 13px; color: ${MUTED}; }
  .cover-editorial .pp-cover-rule { position: absolute; left: 72px; top: 300px; width: 64px; height: 6px; background: ${RED}; }
  .cover-editorial .pp-cover-h1 { left: 72px; top: 330px; width: 632px; }
  .cover-editorial .pp-cover-sub { position: absolute; left: 72px; right: 72px; top: 660px; font-size: 18px; line-height: 1.4; font-weight: 500; }
  .cover-editorial .pp-cover-meta { position: absolute; left: 72px; right: 72px; top: 872px; display: grid; grid-template-columns: repeat(4, 150px); column-gap: 18px; }
  .cover-editorial .pp-cover-meta .k { font-size: 9px; line-height: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: ${MUTED}; margin-bottom: 9px; }
  .cover-editorial .pp-cover-meta .v { font-size: 13px; line-height: 15px; font-weight: 600; }

  .pp.cover-swiss { background: ${INK}; color: #FFFFFF; }
  .cover-swiss .pp-cover-square { position: absolute; left: 0; top: 0; width: 176px; height: 176px; }
  .cover-swiss .pp-cover-ref { position: absolute; left: 386px; top: 56px; font-size: 12px; line-height: 1.5; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
  .cover-swiss .pp-cover-h1 { left: 56px; top: 360px; width: 704px; font-size: 72px; letter-spacing: -0.025em; color: #FFFFFF; }
  .cover-swiss .pp-cover-h1.s { font-size: 58px; }
  .cover-swiss .pp-cover-h1.xs { font-size: 44px; }
  .cover-swiss .pp-cover-rule { position: absolute; left: 56px; top: 760px; width: 96px; height: 6px; background: ${RED}; }
  .cover-swiss .pp-cover-sub { position: absolute; left: 56px; top: 790px; width: 360px; font-size: 18px; line-height: 1.35; font-weight: 700; }
  .cover-swiss .pp-cover-meta-lines { position: absolute; left: 456px; top: 790px; width: 304px; font-size: 13px; line-height: 1.6; color: #9E9E99; }
  .cover-swiss .pp-cover-lockup { position: absolute; left: 56px; top: 946px; }

  .pp.cover-bold { background: ${RED}; color: #FFFFFF; }
  .cover-bold .pp-cover-lockup { position: absolute; left: 64px; top: 64px; }
  .cover-bold .pp-cover-h1 { left: 64px; top: 300px; width: 688px; font-size: 72px; letter-spacing: -0.025em; color: #FFFFFF; }
  .cover-bold .pp-cover-h1.s { font-size: 58px; }
  .cover-bold .pp-cover-h1.xs { font-size: 44px; }
  .cover-bold .pp-cover-band { position: absolute; left: 0; right: 0; top: 836px; bottom: 0; background: ${INK}; padding: 40px 64px 0; }
  .cover-bold .pp-cover-sub { font-size: 22px; line-height: 26px; font-weight: 700; margin-bottom: 14px; }
  .cover-bold .pp-cover-meta-lines { font-size: 13px; line-height: 1.6; color: #BFBFBD; }

  /* ── At a glance ───────────────────────────────────────────────────── */

  .pp-glance .pp-head { margin-bottom: 67px; }
  .pp-glance .pp-label { font-size: 11px; line-height: 13px; }
  .look-swiss .pp-glance .pp-label, .look-bold .pp-glance .pp-label { font-size: 10px; line-height: 12px; }
  .pp .pp-figs { margin-bottom: 64px; min-height: 120px; }
  .pp .pp-figs .pp-label { margin-bottom: 10px; }
  .pp .pp-glance-n { font-size: 64px; line-height: 75px; height: 75px; margin-bottom: 5px; font-weight: 900; letter-spacing: -0.02em; display: flex; align-items: baseline; }
  .pp .pp-glance-n .onum { font-size: 64px; }
  .pp .pp-glance-u { font-size: 22px; font-weight: 700; color: ${INK}; margin-left: 20px; letter-spacing: 0; }
  .pp .pp-lists .pp-label { margin-bottom: 15px; }
  .pp .pp-lists .pp-p { font-size: 14px; line-height: 1.55; }
  .pp .pp-nextstep { position: absolute; left: 0; right: 0; top: 531px; height: 120px; margin: 0 -24px; padding: 20px 24px; display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
  .pp .pp-nextstep .pp-label { margin-bottom: 9px; }
  .pp .pp-next-t { font-size: 18px; font-weight: 700; line-height: 1.35; max-width: 492px; }
  .pp .pp-next-v { font-size: 13px; color: ${MUTED}; text-align: right; line-height: 1.5; flex: none; margin-top: 22px; }
  .look-swiss .pp-glance .pp-head { margin-bottom: 81px; }
  .look-swiss .pp-glance .pp-h1 { font-size: 34px; line-height: 1.05; }
  .look-swiss .pp-glance .pp-h1.long, .look-bold .pp-glance .pp-h1.long { font-size: 28px; }
  .look-swiss .pp .pp-glance-n, .look-bold .pp .pp-glance-n { font-size: 60px; line-height: 71px; height: 71px; }
  .look-swiss .pp .pp-glance-n .onum, .look-bold .pp .pp-glance-n .onum { font-size: 60px; }
  .look-swiss .pp .pp-glance-u, .look-bold .pp .pp-glance-u { font-size: 20px; }
  .look-swiss .pp .pp-figs { margin-bottom: 79px; }
  .look-swiss .pp .pp-lists .pp-p, .look-bold .pp .pp-lists .pp-p { font-size: 13px; }
  .look-swiss .pp .pp-nextstep { top: 558px; height: 112px; }
  .look-swiss .pp .pp-next-t, .look-bold .pp .pp-next-t { font-size: 16px; max-width: 504px; }
  .look-swiss .pp .pp-next-v, .look-bold .pp .pp-next-v { font-size: 12px; }
  .look-bold .pp-glance .pp-head { margin: 58px 0 63px; }
  .look-bold .pp-glance .pp-h1 { line-height: 1.06; }
  .look-bold .pp .pp-figs { margin-bottom: 57px; }
  .look-bold .pp .pp-nextstep { top: 561px; height: 112px; }

  /* ── Letter ────────────────────────────────────────────────────────── */

  .pp .pp-dear { font-size: 28px; line-height: 33px; font-weight: 700; letter-spacing: 0; }
  .pp-letter .pp-head { margin-bottom: 42px; }
  .pp .pp-letter-grid { display: grid; grid-template-columns: 470px 1fr; column-gap: 76px; }
  .pp .pp-letter-body p { font-size: 14px; line-height: 1.55; margin-bottom: 14px; }
  .pp .pp-pull p { font-size: 16px; line-height: 1.4; font-weight: 700; color: ${RED}; }
  .pp .pp-sign { margin: 28px 0 0; }
  .pp .pp-sig-img { display: block; height: 44px; margin-bottom: 4px; }
  .pp .pp-sign-n { font-size: 20px; line-height: 24px; font-weight: 700; margin-bottom: 4px; }
  .pp .pp-sign-r { font-size: 12px; line-height: 14px; color: ${MUTED}; }
  .pp .pp-toc { margin-top: 50px; }
  .pp .pp-toc .pp-label { margin-bottom: 16px; }
  .pp .pp-toc-cols { display: grid; grid-template-columns: 316px 316px; column-gap: 40px; }
  .pp .pp-toc-row { display: flex; justify-content: space-between; font-size: 13px; line-height: 15px; margin-bottom: 10.5px; }
  .pp .pp-toc-n { color: ${RED}; font-weight: 700; }
  .look-swiss .pp .pp-letter-grid { grid-template-columns: 440px 1fr; column-gap: 40px; }
  .look-swiss .pp-letter .pp-head { margin-bottom: 40px; }
  .look-swiss .pp .pp-dear { font-size: 30px; line-height: 1.08; font-weight: 900; }
  .look-swiss .pp .pp-letter-body p, .look-bold .pp .pp-letter-body p { font-size: 13.5px; line-height: 1.6; }
  .look-swiss .pp .pp-pull p, .look-bold .pp .pp-pull p { font-size: 17px; line-height: 1.25; font-weight: 900; }
  .look-swiss .pp .pp-sign, .look-bold .pp .pp-sign { margin-top: 20px; }
  .look-swiss .pp .pp-sign-n, .look-bold .pp .pp-sign-n { font-size: 17px; line-height: 20px; }
  .look-swiss .pp .pp-sign-r, .look-bold .pp .pp-sign-r { font-size: 11px; line-height: 13px; }
  .look-swiss .pp .pp-toc-cols { grid-template-columns: 332px 332px; }
  .look-swiss .pp .pp-toc-row, .look-bold .pp .pp-toc-row { font-size: 12.5px; margin-bottom: 9px; }
  .look-swiss .pp .pp-toc, .look-bold .pp .pp-toc { margin-top: 34px; }
  .look-bold .pp-letter .pp-head { margin: 58px 0 84px; }
  .look-bold .pp .pp-dear { font-size: 36px; line-height: 38px; font-weight: 900; letter-spacing: -0.012em; }
  .look-bold .pp .pp-letter-grid { grid-template-columns: 430px 228px; column-gap: 30px; }
  .look-bold .pp .pp-pull { background: ${PINK}; padding: 20px; min-height: 180px; align-self: start; }
  .look-bold .pp .pp-toc-cols { grid-template-columns: 324px 324px; }

  /* ── Steps (challenge, next steps) ─────────────────────────────────── */

  .pp .pp-step { display: flex; margin-bottom: 41px; }
  .pp .pp-step-n { font-size: 48px; line-height: 56px; width: 80px; flex: none; margin-top: -6px; }
  .pp .pp-step-t { font-size: 20px; line-height: 24px; font-weight: 700; margin-bottom: 8px; }
  .pp .pp-step-b { font-size: 14px; line-height: 1.55; color: ${MUTED}; }
  .pp .pp-chal .pp-step-b { max-width: 520px; }
  .pp-challenge .pp-head { margin-bottom: 42px; }
  .pp .pp-success { margin-top: -6px; padding-top: 24px; }
  .look-swiss .pp .pp-step { margin-bottom: 58px; }
  .look-bold .pp .pp-step { margin-bottom: 54px; }
  .look-swiss .pp .pp-step-n, .look-bold .pp .pp-step-n { font-size: 44px; line-height: 52px; width: 64px; margin-top: -4px; }
  .look-swiss .pp .pp-step-t, .look-bold .pp .pp-step-t { font-size: 18px; line-height: 21px; margin-bottom: 7px; }
  .look-swiss .pp .pp-step-b, .look-bold .pp .pp-step-b { font-size: 13px; }
  .look-swiss .pp-challenge .pp-head { margin-bottom: 70px; }
  .look-bold .pp-challenge .pp-head { margin: 58px 0 66px; }
  .look-swiss .pp .pp-success, .look-bold .pp .pp-success { margin-top: 0; }

  /* ── Approach ──────────────────────────────────────────────────────── */

  .pp .pp-stage { display: grid; grid-template-columns: 48px 222px 1fr; min-height: 63px; margin-bottom: 59px; }
  .pp .pp-stage-n { font-size: 13px; line-height: 15px; font-weight: 700; color: ${RED}; padding-top: 4px; }
  .pp .pp-stage-t { font-size: 22px; line-height: 26px; font-weight: 700; margin-bottom: 6px; }
  .pp .pp-stage-b { padding-top: 2px; }
  .pp .pp-stage-b .pp-p { font-size: 14px; line-height: 1.55; margin-bottom: 6px; }
  .pp .pp-time { font-size: 11px; line-height: 13px; font-weight: 600; color: ${RED}; }
  .pp-approach .pp-head { margin-bottom: 56px; }
  .pp .pp-stages.tight .pp-stage { margin-bottom: 28px; }
  .look-swiss .pp .pp-stage { grid-template-columns: 40px 210px 1fr; min-height: 56px; margin-bottom: 50px; }
  .look-swiss .pp .pp-stage-n { font-size: 12px; font-weight: 900; }
  .look-swiss .pp .pp-stage-t { font-size: 20px; line-height: 24px; font-weight: 900; }
  .look-swiss .pp .pp-stage-b .pp-p, .look-bold .pp .pp-stage-b .pp-p { font-size: 13px; line-height: 1.5; }
  .look-swiss .pp-approach .pp-head { margin-bottom: 68px; }
  .look-bold .pp .pp-stage { grid-template-columns: 56px 204px 1fr; min-height: 50px; margin-bottom: 46px; }
  .look-bold .pp .pp-stage-n.onum { font-size: 40px; line-height: 47px; padding-top: 0; margin-top: -6px; }
  .look-bold .pp .pp-stage-t { font-size: 19px; line-height: 22px; font-weight: 900; }
  .look-bold .pp-approach .pp-head { margin: 58px 0 62px; }

  /* ── Scope ─────────────────────────────────────────────────────────── */

  .pp-scope .pp-head { margin-bottom: 56px; }
  .pp .pp-svc { display: grid; grid-template-columns: 194px 1fr; margin-bottom: 46px; position: relative; }
  .pp .pp-svc-n { font-size: 34px; line-height: 40px; letter-spacing: -0.01em; margin-bottom: 8px; -webkit-text-stroke-width: 1.2px; }
  .pp .pp-svc-t { font-size: 17px; line-height: 1.2; font-weight: 700; margin-bottom: 8px; padding-right: 20px; }
  .pp .pp-svc-o { font-size: 15px; line-height: 1.35; font-weight: 700; margin: 6px 0 16px; }
  .pp .pp-dl { display: grid; grid-template-columns: 1fr 1fr; column-gap: 24px; }
  .pp .pp-dl .pp-sq li { font-size: 11.5px; line-height: 16px; margin-bottom: 10px; padding-left: 15px; }
  .pp .pp-included { margin-top: 15px; }
  .pp .pp-included .pp-panel-h { margin-bottom: 18px; }
  .pp .pp-included .pp-trio-v { font-size: 12px; }
  .pp .pp-notinc .pp-label { margin-bottom: 10px; }
  .pp .pp-notinc .pp-p { font-size: 12px; line-height: 1.55; color: ${MUTED}; }
  .look-swiss .pp .pp-svc-n, .look-bold .pp .pp-svc-n { margin-bottom: 6px; -webkit-text-stroke-width: 1.3px; }
  .look-swiss .pp .pp-svc-t, .look-bold .pp .pp-svc-t { font-size: 16px; margin-bottom: 6px; }
  .look-swiss .pp .pp-svc-o, .look-bold .pp .pp-svc-o { font-size: 14px; margin-bottom: 12px; }
  .look-swiss .pp .pp-dl .pp-sq li, .look-bold .pp .pp-dl .pp-sq li { line-height: 14px; margin-bottom: 10px; }
  .look-swiss .pp .pp-dl .pp-sq li::before, .look-bold .pp .pp-dl .pp-sq li::before { top: 5px; }
  .look-swiss .pp .pp-svc, .look-bold .pp .pp-svc { margin-bottom: 52px; }
  .look-swiss .pp-scope .pp-head { margin-bottom: 64px; }
  .look-swiss .pp .pp-included .pp-trio-k, .look-bold .pp .pp-included .pp-trio-k { font-size: 12.5px; }
  .look-swiss .pp .pp-included .pp-trio-v, .look-bold .pp .pp-included .pp-trio-v { font-size: 11.5px; }
  .look-bold .pp-scope .pp-head { margin: 58px 0 59px; }

  /* ── Timeline ──────────────────────────────────────────────────────── */

  .pp-timeline .pp-head { margin-bottom: 36px; }
  .pp-timeline .pp-sub { font-size: 13px; margin-top: 8px; }
  .pp .pp-gantt { margin: 0 0 4px; }
  .pp .pp-gantt-row { display: grid; grid-template-columns: 190px 1fr; align-items: center; height: var(--track); margin-bottom: calc(var(--step) - var(--track)); }
  .pp .pp-gantt-row.head { height: 12px; margin-bottom: 16px; }
  .pp .pp-gantt-cols { display: grid; grid-template-columns: repeat(var(--weeks), 1fr); font-size: 10px; line-height: 12px; font-weight: 600; color: ${MUTED}; text-align: center; }
  .pp .pp-gantt-name { font-size: 13px; line-height: 15px; font-weight: 700; padding-right: 12px; }
  .pp .pp-gantt-track { position: relative; height: var(--track); background: var(--panel); }
  .pp .pp-gantt-track i { position: absolute; top: 0; bottom: 0; background: ${INK}; }
  .pp .pp-gantt-track i.last { background: ${RED}; }
  .pp .pp-approve { font-size: 13px; line-height: 15px; font-weight: 700; margin: 0 0 33px; }
  .pp .pp-ms .pp-panel-h { margin-bottom: 18px; }
  .pp .pp-ms .pp-label { margin-bottom: 8px; }
  .pp .pp-ms-t { font-size: 14px; line-height: 1.3; font-weight: 700; }
  .look-swiss .pp .pp-gantt-row { grid-template-columns: 180px 1fr; }
  .look-swiss .pp .pp-gantt-name, .look-bold .pp .pp-gantt-name { font-size: 12px; line-height: 14px; }
  .look-swiss .pp .pp-ms-t, .look-bold .pp .pp-ms-t { font-size: 13px; }
  .look-swiss .pp-timeline .pp-head { margin-bottom: 52px; }
  .look-bold .pp-timeline .pp-head { margin: 58px 0 43px; }
  .look-bold .pp-timeline .pp-sub { margin-top: 14px; }
  .look-bold .pp .pp-gantt-track i { background: ${RED}; }
  .look-bold .pp .pp-gantt-track i.last { background: ${INK}; }

  /* ── Options ───────────────────────────────────────────────────────── */

  .pp-options .pp-head { margin-bottom: 37px; }
  .pp .pp-opts { display: grid; grid-template-columns: repeat(var(--n), 1fr); margin: 0 -16px 38px; }
  .pp .pp-opt { position: relative; padding: 16px 16px 28px; min-height: 360px; }
  .pp .pp-opt.rec { background: var(--panel); }
  .pp .pp-opt.rec::before { content: ''; position: absolute; left: 16px; right: 16px; top: 16px; height: 4px; background: ${RED}; }
  .pp .pp-opt-rec { font-size: 10px; line-height: 12px; height: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${RED}; margin: 16px 0 12px; }
  .pp .pp-opt-t { font-size: 18px; line-height: 1.2; font-weight: 700; margin-bottom: 38px; }
  .pp .pp-opt-p { font-size: 22px; font-weight: 900; margin-bottom: 5px; }
  .pp .pp-opt-p.onum { font-size: 40px; line-height: 47px; letter-spacing: -0.01em; }
  .pp .pp-opt .pp-label { font-size: 12px; line-height: 14px; letter-spacing: 0.06em; margin-bottom: 32px; }
  .pp .pp-opt .pp-p { font-size: 13px; line-height: 1.45; margin-bottom: 14px; }
  .pp .pp-opt .pp-dash li { font-size: 13px; line-height: 1.3; margin-bottom: 17px; padding-left: 20px; }
  .pp-options .pp-body > .pp-label { margin-bottom: 22px; }
  .pp .pp-incl { column-gap: 0; grid-template-columns: 336px 1fr; }
  .pp .pp-incl .pp-sq li { margin-bottom: 14px; }
  .look-swiss .pp .pp-opt.rec::before, .look-bold .pp .pp-opt.rec::before { display: none; }
  .look-swiss .pp .pp-opt-rec, .look-bold .pp .pp-opt-rec { margin: 0 0 8px; }
  .look-swiss .pp .pp-opt-t, .look-bold .pp .pp-opt-t { font-size: 17px; margin-bottom: 14px; }
  .look-swiss .pp .pp-opt-p.onum, .look-bold .pp .pp-opt-p.onum { font-size: 36px; line-height: 42px; margin-bottom: 6px; }
  .look-swiss .pp .pp-opt .pp-label, .look-bold .pp .pp-opt .pp-label { font-size: 10px; line-height: 12px; margin-bottom: 20px; }
  .look-swiss .pp .pp-opt .pp-sq li, .look-bold .pp .pp-opt .pp-sq li { font-size: 12px; line-height: 14px; margin-bottom: 16px; }
  .look-swiss .pp .pp-opt, .look-bold .pp .pp-opt { min-height: 330px; }
  .look-swiss .pp-options .pp-head { margin-bottom: 45px; }
  .look-bold .pp-options .pp-head { margin: 58px 0 69px; }

  /* ── Investment ────────────────────────────────────────────────────── */

  .pp .pp-inv-hero { margin-bottom: 76px; }
  .pp .pp-inv-name { font-size: 22px; line-height: 26px; font-weight: 700; margin-bottom: 8px; }
  .pp .pp-inv-price { font-size: 96px; line-height: 113px; letter-spacing: -0.03em; -webkit-text-stroke-width: 2px; margin-bottom: 13px; }
  .pp .pp-inv-cap { font-size: 14px; line-height: 16px; color: ${MUTED}; }
  .pp .pp-split { display: grid; grid-template-columns: repeat(3, var(--cw)); column-gap: 24px; margin-bottom: 75px; }
  .pp .pp-split-p { font-size: 36px; line-height: 42px; margin-bottom: 8px; letter-spacing: 0; }
  .pp .pp-split-a { font-size: 16px; line-height: 19px; font-weight: 700; margin-bottom: 5px; }
  .pp .pp-split-l { font-size: 12px; line-height: 14px; color: ${MUTED}; }
  .pp .pp-lines { padding: 18px 24px 14px; margin-bottom: 20px; }
  .pp .pp-lines .pp-label { font-size: 11px; line-height: 13px; margin-bottom: 8px; }
  .look-bold .pp .pp-lines .pp-label { color: ${RED}; }
  .pp .pp-line { display: flex; justify-content: space-between; gap: 18px; font-size: 16px; line-height: 19px; padding: 10px 0 11px; }
  .pp .pp-line-a { font-weight: 700; white-space: nowrap; }
  .pp .pp-line.pp-muted { font-size: 12px; }
  .pp .pp-covers { margin: 0 0 4px; }
  .pp .pp-covers .pp-panel-h { margin-bottom: 16px; }
  .pp .pp-cover-i { position: relative; padding-left: 14px; }
  .pp .pp-cover-i::before { content: ''; position: absolute; left: 0; top: 6px; width: 5px; height: 5px; background: ${RED}; }
  .pp .pp-cover-k { font-size: 13px; line-height: 15px; font-weight: 700; margin-bottom: 7px; }
  .pp .pp-cover-v { font-size: 12px; line-height: 1.5; color: ${MUTED}; }
  .pp .pp-inv-note p { font-size: 12.5px; line-height: 1.55; color: ${MUTED}; }
  .look-swiss .pp-investment .pp-head { margin-bottom: 68px; }
  .look-swiss .pp .pp-inv-hero { margin-bottom: 72px; }
  .look-swiss .pp .pp-inv-price { margin-bottom: 7px; }
  .look-swiss .pp .pp-inv-cap { font-size: 13px; }
  .look-swiss .pp .pp-split, .look-bold .pp .pp-split { margin-bottom: 70px; }
  .look-swiss .pp .pp-split-p, .look-bold .pp .pp-split-p { font-size: 34px; line-height: 40px; margin-bottom: 6px; }
  .look-swiss .pp .pp-split-a, .look-bold .pp .pp-split-a { font-size: 15px; line-height: 18px; margin-bottom: 4px; }
  .look-swiss .pp .pp-split-l, .look-bold .pp .pp-split-l { font-size: 11.5px; }
  .look-swiss .pp .pp-line { font-size: 14px; line-height: 16px; padding: 10px 0; }
  .look-bold .pp .pp-line { font-size: 14px; line-height: 16px; padding: 8px 0; }
  .look-swiss .pp .pp-lines .pp-label, .look-bold .pp .pp-lines .pp-label { font-size: 10px; line-height: 12px; }
  .look-swiss .pp .pp-cover-k, .look-bold .pp .pp-cover-k { font-size: 12.5px; }
  .look-swiss .pp .pp-cover-v, .look-bold .pp .pp-cover-v { font-size: 11.5px; }
  .look-swiss .pp .pp-covers, .look-bold .pp .pp-covers { margin-top: 50px; }
  .look-bold .pp.pp-investment:not(.pp-inv-cont)::before { content: ''; position: absolute; left: 0; right: 0; top: 0; height: 440px; background: ${RED}; }
  .look-bold .pp.pp-investment:not(.pp-inv-cont) .pp-kicker { color: #FFFFFF; }
  .look-bold .pp.pp-investment:not(.pp-inv-cont) .pp-giant { display: none; }
  .look-bold .pp .pp-inv-block { color: #FFFFFF; margin: 58px 0 144px; }
  .look-bold .pp .pp-inv-block .pp-inv-name { font-size: 24px; line-height: 28px; margin-bottom: 2px; color: #FFFFFF; }
  .look-bold .pp .pp-inv-block .pp-inv-price { font-size: 110px; line-height: 129px; letter-spacing: -0.04em; margin-bottom: 51px; }
  .look-bold .pp .pp-inv-block .pp-inv-cap { color: #FFFFFF; }

  /* ── Case studies, proof ───────────────────────────────────────────── */

  .pp .pp-img { background: var(--panel) center / cover no-repeat; height: 330px; margin-bottom: 70px; }
  .look-swiss .pp .pp-img, .look-bold .pp .pp-img { height: 300px; margin-bottom: 42px; }
  .pp .pp-img.tall { height: 480px; margin: 0; }
  .pp .pp-img.portrait { height: 350px; margin: 0; }
  .look-swiss .pp .pp-img.portrait, .look-bold .pp .pp-img.portrait { height: 330px; }
  .pp .pp-img + .pp-label, .pp .pp-split2 .pp-label { margin-bottom: 9px; font-size: 11px; line-height: 13px; }
  .pp .pp-h1-case { font-size: 28px; line-height: 1.12; margin-bottom: 30px; }
  .pp .pp-cols-text { column-count: 2; column-gap: 32px; }
  .pp .pp-cols-text p { font-size: 12.5px; line-height: 1.6; color: ${MUTED}; }
  .pp .pp-stats { display: grid; grid-template-columns: repeat(3, var(--cw)); column-gap: 24px; margin: 36px 0; }
  .pp .pp-stat-n { font-size: 64px; line-height: 75px; margin-bottom: 5px; }
  .look-swiss .pp .pp-stat-n, .look-bold .pp .pp-stat-n { font-size: 60px; line-height: 71px; }
  .pp .pp-stat-c { font-size: 12px; line-height: 14px; color: ${MUTED}; }
  .pp .pp-split2 { display: grid; grid-template-columns: 300px 1fr; gap: 32px; }
  .pp .pp-split2 .pp-h1-case { font-size: 30px; line-height: 1.08; }
  .pp .pp-split2 .pp-prose p { font-size: 13px; line-height: 1.6; }
  .pp .pp-quote { font-size: 17px; font-weight: 700; line-height: 1.35; margin: 24px 0 10px; }
  .pp .pp-q .pp-quote { margin-top: 0; }
  .look-swiss .pp .pp-split2 .pp-quote, .look-bold .pp .pp-split2 .pp-quote { color: ${RED}; font-size: 16px; }

  /* ── Profiles ──────────────────────────────────────────────────────── */

  .pp .pp-prof { display: grid; grid-template-columns: 280px 1fr; column-gap: 40px; margin: 8px 0 40px; }
  .pp .pp-prof.noimg { grid-template-columns: 1fr; max-width: 560px; }
  .pp .pp-prof .pp-label { margin-bottom: 10px; }
  .pp .pp-prof-n { font-size: 34px; line-height: 1.05; letter-spacing: -0.01em; }
  .pp .pp-bio p { font-size: 13px; line-height: 1.6; color: ${MUTED}; }
  .pp .pp-tasks .pp-panel-h { margin-bottom: 22px; }
  .pp .pp-tasks .pp-sq li { font-size: 12.5px; line-height: 15px; margin-bottom: 15px; }
  .pp .pp-tasks .pp-cols2 { column-gap: 40px; }
  .pp .pp-contactline { position: absolute; left: 0; right: 0; top: 825px; font-size: 12px; line-height: 1.55; }
  .look-swiss .pp .pp-prof, .look-bold .pp .pp-prof { grid-template-columns: 260px 1fr; column-gap: 32px; margin-top: 0; }
  .look-swiss .pp .pp-prof.noimg, .look-bold .pp .pp-prof.noimg { grid-template-columns: 1fr; }
  .look-swiss .pp .pp-prof-n, .look-bold .pp .pp-prof-n { font-size: 32px; }
  .look-swiss .pp .pp-lead, .look-bold .pp .pp-lead { font-size: 14px; }
  .look-swiss .pp .pp-bio p, .look-bold .pp .pp-bio p { font-size: 12.5px; }
  .look-swiss .pp .pp-contactline, .look-bold .pp .pp-contactline { top: 726px; }

  /* ── Working together ──────────────────────────────────────────────── */

  .pp-working .pp-head { margin-bottom: 62px; }
  .pp .pp-work { column-gap: 40px; margin-bottom: 42px; }
  .pp .pp-work .pp-panel-h { margin-bottom: 19px; }
  .pp .pp-sq.big li { margin-bottom: 18px; }
  .pp .pp-li-t { font-size: 12.5px; line-height: 15px; font-weight: 700; margin-bottom: 4px; }
  .pp .pp-li-b { font-size: 11.5px; line-height: 1.5; color: ${MUTED}; }
  .pp .pp-rev { padding: 20px 24px; }
  .pp .pp-rev .pp-panel-h { font-size: 13px; line-height: 15px; margin-bottom: 7px; }
  .pp .pp-rev .pp-p { font-size: 11.5px; line-height: 1.5; color: ${MUTED}; }
  .look-swiss .pp-working .pp-head { margin-bottom: 84px; }
  .look-swiss .pp .pp-sq.big li, .look-bold .pp .pp-sq.big li { margin-bottom: 14px; }
  .look-swiss .pp .pp-work .pp-panel-h, .look-bold .pp .pp-work .pp-panel-h { margin-bottom: 16px; }
  .look-bold .pp-working .pp-head { margin: 58px 0 78px; }
  .look-bold .pp-working .pp-h1, .look-bold .pp-terms .pp-h1 { font-size: 36px; line-height: 1.06; }

  /* ── The fine print ────────────────────────────────────────────────── */

  .pp-terms .pp-head { margin-bottom: 45px; }
  .pp-terms .pp-sub { font-size: 13px; margin-top: 10px; }
  .pp .pp-terms.pp-cols2 { column-gap: 40px; }
  .pp .pp-term { display: grid; grid-template-columns: 30px 1fr; margin-bottom: 20px; }
  .pp .pp-term-n { font-size: 11px; line-height: 13px; font-weight: 700; color: ${RED}; padding-top: 1px; }
  .pp .pp-term-t { font-size: 12.5px; line-height: 15px; font-weight: 700; margin-bottom: 4px; }
  .pp .pp-term-b { font-size: 11px; line-height: 1.5; color: ${MUTED}; }
  .pp .pp-ask { font-size: 12.5px; line-height: 15px; font-weight: 700; margin-top: 26px; }
  .look-swiss .pp .pp-term, .look-bold .pp .pp-term { grid-template-columns: 28px 1fr; margin-bottom: 18px; }
  .look-swiss .pp-terms .pp-head { margin-bottom: 84px; }
  .look-bold .pp-terms .pp-head { margin: 58px 0 84px; }
  .look-bold .pp-terms .pp-sub { margin-top: 14px; }

  /* ── Next steps ────────────────────────────────────────────────────── */

  .pp-next .pp-head { margin-bottom: 51px; }
  .pp .pp-steps .pp-step { margin-bottom: 43px; }
  .pp .pp-steps .pp-step-n { font-size: 40px; line-height: 47px; width: 64px; margin-top: -4px; }
  .pp .pp-sigs { display: grid; grid-template-columns: 306px 306px; column-gap: 30px; margin: -6px 0 32px; }
  .pp .pp-sig-space { height: 0; position: relative; }
  .pp .pp-sig-space img { position: absolute; bottom: 4px; left: 0; max-height: 40px; }
  .pp .pp-sig-line { height: 0; border-top: 1px solid ${INK}; margin-bottom: 9px; }
  .pp .pp-sig-k { font-size: 11px; line-height: 13px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: ${MUTED}; }
  .pp .pp-sig-who { font-size: 11px; line-height: 13px; color: ${INK}; margin-top: 6px; }
  .pp .pp-sig-d { font-size: 11px; line-height: 13px; color: ${MUTED}; margin-top: 18px; }
  .pp .pp-contact { display: grid; grid-template-columns: 300px 1fr; align-items: start; padding: 16px 24px 18px; min-height: 128px; }
  .pp .pp-contact-l { padding-top: 4px; }
  .pp .pp-contact-n { font-size: 15px; line-height: 18px; font-weight: 700; margin-bottom: 4px; }
  .pp .pp-contact-role { font-size: 11px; line-height: 13px; color: ${MUTED}; margin-bottom: 9px; }
  .pp .pp-contact-lines div { font-size: 12px; line-height: 1.5; }
  .pp .pp-acc { margin: -20px 0 26px; }
  .look-swiss .pp-next .pp-head { margin-bottom: 48px; }
  .look-swiss .pp-next .pp-h1-xl { font-size: 30px; line-height: 1.08; letter-spacing: -0.01em; }
  .look-swiss .pp .pp-steps .pp-step, .look-bold .pp .pp-steps .pp-step { margin-bottom: 42px; }
  .look-swiss .pp .pp-steps .pp-step-n, .look-bold .pp .pp-steps .pp-step-n { width: 60px; }
  .look-swiss .pp .pp-sigs { grid-template-columns: 332px 332px; column-gap: 40px; margin-top: 22px; }
  .look-swiss .pp .pp-sig-k, .look-bold .pp .pp-sig-k { font-size: 10px; line-height: 12px; letter-spacing: 0.08em; }
  .look-swiss .pp .pp-sig-d, .look-bold .pp .pp-sig-d { font-size: 10px; margin-top: 14px; }
  .look-swiss .pp .pp-contact { margin-top: 20px; }
  .look-swiss .pp .pp-contact-n, .look-bold .pp .pp-contact-n { font-size: 14px; line-height: 16px; }
  .look-bold .pp-next .pp-head { margin: 58px 0 40px; }
  .look-bold .pp-next .pp-h1-xl { font-size: 36px; line-height: 1.06; letter-spacing: -0.012em; }
  .look-bold .pp .pp-steps .pp-step { margin-bottom: 33px; }
  .look-bold .pp .pp-sigs { grid-template-columns: 324px 324px; column-gap: 40px; margin-top: 22px; }
  .pp .pp-redband {
    position: absolute; left: 0; right: 0; top: 846px; bottom: 0; display: grid; grid-template-columns: 320px 1fr;
    align-items: start; background: ${RED}; color: #FFFFFF; padding: 32px 64px 0; z-index: 1;
  }
  .pp .pp-redband .pp-contact-l { padding-top: 2px; }
  .pp .pp-redband .pp-contact-role, .pp .pp-redband .pp-contact-lines div { color: #FFFFFF; }

  /* ── Library pages ─────────────────────────────────────────────────── */
  .pp .pp-prose { max-width: 600px; }

  /* ── Editing on the canvas ─────────────────────────────────────────── */
  body.editing .pp [data-drop] { position: absolute; right: -30px; top: 0; }
  body.editing .pp [data-add] { position: relative; z-index: 3; }
  @media print {
    body.look-editorial, body.look-swiss, body.look-bold { background: #FFFFFF; padding: 0; }
    .page.pp { margin: 0 !important; box-shadow: none !important; }
  }
`
